import React, { useRef, useEffect, useState, useCallback } from 'react'
import useMenuChat from './useMenuChat'
import executeOperations from './executeOperations'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import OperationList from './OperationList'

function ChatCMS({ shop, allDocs }) {
  const {
    displayMessages,
    isLoading,
    error,
    pendingProposal,
    sendMessage,
    clearProposal,
    resetChat,
  } = useMenuChat(shop)

  const [isExecuting, setIsExecuting] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [displayMessages, pendingProposal])

  const handleApprove = useCallback(async () => {
    if (!pendingProposal) return
    setIsExecuting(true)
    setFeedback(null)

    try {
      const result = await executeOperations(pendingProposal.operations, allDocs)
      if (result.success) {
        setFeedback({ type: 'success', text: `Applied ${pendingProposal.operations.length} change(s) successfully.` })
      } else {
        setFeedback({ type: 'error', text: `Failed: ${result.error}` })
      }
    } catch (err) {
      setFeedback({ type: 'error', text: `Failed: ${err.message}` })
    } finally {
      setIsExecuting(false)
      clearProposal()
    }
  }, [pendingProposal, allDocs, clearProposal])

  const handleReject = useCallback(() => {
    clearProposal()
    setFeedback({ type: 'info', text: 'Changes rejected. You can describe what you\'d like instead.' })
  }, [clearProposal])

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-3xl mx-auto">
          {displayMessages.length === 0 && !isLoading && (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <div className="text-center text-gray-400">
                <div className="text-lg font-medium mb-1">ChatCMS</div>
                <div className="text-sm">Describe a menu change in plain English.</div>
              </div>
            </div>
          )}

          {displayMessages.map((msg, i) => {
            if (msg.operations) {
              return (
                <OperationList
                  key={i}
                  operations={msg.operations}
                  understanding={msg.text}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  isExecuting={isExecuting}
                />
              )
            }
            return <ChatMessage key={i} message={msg} />
          })}

          {isLoading && (
            <div className="flex justify-start mb-3">
              <div className="bg-gray-100 rounded-2xl px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full" />
                  Thinking...
                </div>
              </div>
            </div>
          )}

          {feedback && (
            <div className={`mb-3 px-4 py-2 rounded-xl text-sm ${
              feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
              feedback.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
              'bg-gray-50 text-gray-600 border border-gray-200'
            }`}>
              {feedback.text}
            </div>
          )}

          {error && (
            <div className="mb-3 px-4 py-2 rounded-xl text-sm bg-red-50 text-red-700 border border-red-200">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <ChatInput onSendMessage={sendMessage} isLoading={isLoading || isExecuting} />

      {/* New chat button — small, top-right corner */}
      {displayMessages.length > 0 && (
        <button
          onClick={resetChat}
          className="
            absolute top-2 right-3
            px-3 py-1 text-xs font-medium
            text-gray-500 hover:text-gray-900
            border border-gray-200 hover:border-gray-400
            rounded-lg bg-white
            transition-colors
          "
        >
          New chat
        </button>
      )}
    </div>
  )
}

export default ChatCMS
